// The suitability refresh of an existing client (SEP-288 sections 3 to 5).
//
// A refresh is the work around one new V6 SUI: the issue to the client, the
// reminders, the Internal Suitability Refresh Checklist, the outcome, the
// Compliance decision, and the letter or the mandate that the outcome needs.
//
// This module holds the closed value sets and the pure checklist rule that the
// server and the browser read in the same way. The checklist mirrors the firm
// form "Internal Suitability Refresh Checklist - Existing Clients" (08.2026),
// which the Linear card attaches: the firm needs no paper copy of it.

import { z } from 'zod';

import {
    TOTAL_EXPENDITURE_COMPONENT_IDS,
    TOTAL_INCOME_COMPONENT_IDS,
    FACT_FIND_ENTITY_TOTALS,
} from './fact-find-totals';

/** The lifecycle of one refresh. */
export const suitabilityRefreshStatusSchema = z.enum([
    // The draft is open, and nobody has issued it to the client.
    'in_progress',
    // The draft went to the client. The reminder clock runs.
    'issued',
    // The client returned the fact find. The reminders stop.
    'returned',
    // The IM or the EA chose an outcome, and Compliance must decide.
    'pending_compliance',
    // Compliance rejected the outcome. The IM or the EA amends and submits again.
    'returned_for_amendment',
    // Compliance approved Outcome B. The new mandate is still outstanding.
    'mandate_required',
    // The new mandate is ready, and Compliance must approve it.
    'pending_mandate_approval',
    // Compliance approved the outcome (and, for Outcome B, the new mandate).
    'completed',
    // The IM or the EA cancelled the draft.
    'cancelled',
]);
export type SuitabilityRefreshStatus = z.infer<typeof suitabilityRefreshStatusSchema>;

/** The statuses that come before the outcome, where the reminder clock can run. */
export const SUITABILITY_REFRESH_OPEN_STATUSES: readonly SuitabilityRefreshStatus[] = [
    'in_progress',
    'issued',
    'returned',
    'returned_for_amendment',
];

/** The two outcomes of a refresh. */
export const suitabilityRefreshOutcomeSchema = z.enum(['A', 'B']);
export type SuitabilityRefreshOutcome = z.infer<typeof suitabilityRefreshOutcomeSchema>;

export const SUITABILITY_REFRESH_OUTCOME_LABELS: Record<SuitabilityRefreshOutcome, string> = {
    A: 'Outcome A - No / Minor Changes',
    B: 'Outcome B - Significant Changes / New Mandate Required',
};

/** The change that one area of the fact find shows against the previous one. */
export const refreshChangeLevelSchema = z.enum(['none', 'minor', 'significant']);
export type RefreshChangeLevel = z.infer<typeof refreshChangeLevelSchema>;

/** Section 2 of the checklist: the twelve areas, in the order of the form. */
export const REFRESH_CHECKLIST_AREAS = [
    { key: 'personal_circumstances', label: 'Personal Circumstances' },
    { key: 'employment_retirement', label: 'Employment / Retirement' },
    { key: 'assets_liabilities', label: 'Assets & Liabilities' },
    { key: 'income_expenditure', label: 'Income & Expenditure' },
    { key: 'source_of_wealth', label: 'Source of Wealth / Funds' },
    { key: 'health_vulnerability', label: 'Health / Vulnerability' },
    { key: 'liquidity', label: 'Liquidity Requirements' },
    { key: 'investment_objectives', label: 'Investment Objectives' },
    { key: 'knowledge_experience', label: 'Knowledge & Experience' },
    { key: 'risk_tolerance', label: 'Risk Tolerance' },
    { key: 'capacity_for_loss', label: 'Capacity for Loss' },
    { key: 'investment_restrictions', label: 'Investment Restrictions' },
] as const;
export type RefreshChecklistAreaKey = (typeof REFRESH_CHECKLIST_AREAS)[number]['key'];

/** Section 3 of the checklist: the four suitability confirmations. */
export const REFRESH_CHECKLIST_CONFIRMATIONS = [
    {
        key: 'liquidity_appropriate',
        label: "Is the client's Liquidity Situation still appropriate?",
    },
    { key: 'service_suitable', label: 'Is the Service still suitable for this client?' },
    { key: 'risk_profile_suitable', label: 'Is the Risk Profile still suitable for this client?' },
    { key: 'strategy_suitable', label: 'Is the Investment Strategy still suitable?' },
] as const;
export type RefreshChecklistConfirmationKey =
    (typeof REFRESH_CHECKLIST_CONFIRMATIONS)[number]['key'];

/** The declaration of section 5, in the words of the form. */
export const REFRESH_DECLARATION_TEXT =
    "I confirm that I have reviewed the client's updated Suitability Fact Find, assessed the impact of any changes on the suitability of the existing Investment Strategy and completed this assessment in accordance with the Existing Client Suitability Refresh Procedure.";

export const refreshChecklistSchema = z.object({
    /** Section 2: one change level and one rationale for each area. */
    areas: z.record(
        z.string(),
        z.object({
            change: refreshChangeLevelSchema.nullable(),
            rationale: z.string(),
        }),
    ),
    /** Section 3: yes (true), no (false), or not answered (null). */
    confirmations: z.record(z.string(), z.boolean().nullable()),
    /** Section 5: who made the declaration, and when. */
    declaredBy: z.string().nullable(),
    declaredAt: z.string().nullable(),
});
export type RefreshChecklist = z.infer<typeof refreshChecklistSchema>;

/** An empty checklist. */
export function emptyRefreshChecklist(): RefreshChecklist {
    return {
        areas: Object.fromEntries(
            REFRESH_CHECKLIST_AREAS.map((area) => [area.key, { change: null, rationale: '' }]),
        ),
        confirmations: Object.fromEntries(
            REFRESH_CHECKLIST_CONFIRMATIONS.map((c) => [c.key, null]),
        ),
        declaredBy: null,
        declaredAt: null,
    };
}

/**
 * The problems that stop the submission of an outcome. An empty list means
 * that the checklist is complete for that outcome.
 *
 * The rules come from the form:
 *   - each area needs a change level;
 *   - a minor or a significant change needs its rationale;
 *   - each confirmation needs a yes or a no;
 *   - a significant change means Outcome B ("new Investment Mandate required");
 *   - the summary of why the outcome was reached is required;
 *   - the IM declaration is required.
 */
export function refreshChecklistProblems(
    checklist: RefreshChecklist,
    outcome: SuitabilityRefreshOutcome | null,
    summary: string,
): string[] {
    const problems: string[] = [];
    let significant = false;
    for (const area of REFRESH_CHECKLIST_AREAS) {
        const entry = checklist.areas[area.key];
        if (!entry || entry.change === null) {
            problems.push(`${area.label}: choose No Change, Minor Change or Significant Change`);
            continue;
        }
        if (entry.change !== 'none' && entry.rationale.trim() === '') {
            problems.push(`${area.label}: give the rationale for the change`);
        }
        if (entry.change === 'significant') significant = true;
    }
    for (const confirmation of REFRESH_CHECKLIST_CONFIRMATIONS) {
        const answer = checklist.confirmations[confirmation.key];
        if (answer === null || answer === undefined) {
            problems.push(`${confirmation.label} Answer Yes or No`);
        }
    }
    if (outcome === null) problems.push('Choose Outcome A or Outcome B');
    if (outcome === 'A' && significant) {
        problems.push('A significant change needs Outcome B (a new Investment Mandate)');
    }
    if (summary.trim() === '') {
        problems.push('Give a brief summary of why the selected outcome has been reached');
    }
    if (!checklist.declaredBy || !checklist.declaredAt) {
        problems.push('Make the Investment Manager declaration');
    }
    return problems;
}

/** Where one refresh stands with Compliance, as the SUI list shows it. */
export function refreshComplianceStatus(
    status: SuitabilityRefreshStatus,
): 'not_submitted' | 'pending' | 'approved' | 'rejected' {
    switch (status) {
        case 'pending_compliance':
        case 'pending_mandate_approval':
            return 'pending';
        case 'mandate_required':
        case 'completed':
            return 'approved';
        case 'returned_for_amendment':
            return 'rejected';
        default:
            return 'not_submitted';
    }
}

// ---------------------------------------------------------------------------
// The change summary of a refresh (SEP-351)
// ---------------------------------------------------------------------------

/** The capacity-for-loss questions of the risk instrument. The others are risk tolerance. */
const CAPACITY_FOR_LOSS_QUESTIONS: ReadonlySet<string> = new Set([
    'atr_q7',
    'atr_q8',
    'atr_q9',
    'atr_q10',
    'atr_q11',
    'atr_q12',
]);

/** The questions of the person that are about employment or retirement. */
const EMPLOYMENT_QUESTIONS: ReadonlySet<string> = new Set([
    'employment_status',
    'employment_status_other',
    'occupation',
    'employer',
    'employer_address',
]);

/** The income and expenditure rows of each form. Each other money row is an asset or a liability. */
const INCOME_EXPENDITURE_QUESTIONS: ReadonlySet<string> = new Set([
    ...TOTAL_INCOME_COMPONENT_IDS,
    ...TOTAL_EXPENDITURE_COMPONENT_IDS,
    ...FACT_FIND_ENTITY_TOTALS.filter(
        (t) => t.totalId === 'total_income' || t.totalId === 'total_expenditure',
    ).flatMap((t) => t.componentIds),
]);

/** The id of the money row of a "% ownership" or a "Details or notes" box. */
function moneyRowOf(questionId: string): string {
    return questionId.replace(/_(ownership|detail)$/, '');
}

/**
 * The area of the Internal Suitability Refresh Checklist that one changed
 * answer belongs to (SEP-351). The change summary links each change to the
 * rationale of its area, so the IM or the EA gives the rationale there.
 *
 * The section of the form decides first. Inside a section, a question can
 * belong to a narrower area: the investment objective, the employment of a
 * person, an income row, a capacity-for-loss question.
 */
export function refreshChecklistAreaFor(
    sectionId: string,
    questionId: string,
): RefreshChecklistAreaKey {
    switch (sectionId) {
        case 'personal':
            return EMPLOYMENT_QUESTIONS.has(questionId)
                ? 'employment_retirement'
                : 'personal_circumstances';
        case 'health_wellbeing':
            return 'health_vulnerability';
        case 'knowledge':
        case 'objectives_knowledge':
            return questionId.startsWith('investment_objective')
                ? 'investment_objectives'
                : 'knowledge_experience';
        case 'financial_situation':
            return INCOME_EXPENDITURE_QUESTIONS.has(moneyRowOf(questionId))
                ? 'income_expenditure'
                : 'assets_liabilities';
        case 'source_of_wealth':
            return 'source_of_wealth';
        case 'liquidity':
        case 'account_usage_liquidity':
            return 'liquidity';
        case 'risk_assessment':
            return CAPACITY_FOR_LOSS_QUESTIONS.has(questionId)
                ? 'capacity_for_loss'
                : 'risk_tolerance';
        case 'investment_restrictions':
            return 'investment_restrictions';
        default:
            // The entity information, the estate planning and the declaration
            // describe the circumstances of the client.
            return 'personal_circumstances';
    }
}

/** One changed answer, as the change summary shows it. */
export const factFindAnswerChangeSchema = z.object({
    questionId: z.string(),
    sectionId: z.string(),
    sectionTitle: z.string(),
    label: z.string(),
    /** The answer of the last completed fact find. Empty when it had none. */
    before: z.string(),
    /** The answer on file in the new fact find. Empty when it has none. */
    after: z.string(),
    /** The area of the checklist whose rationale covers this change. */
    checklistArea: z.string(),
    /** The sentence of SCRIVENER about the change. Null before a run, or when the run is out of date. */
    explanation: z.string().nullable(),
});
export type FactFindAnswerChangeDto = z.infer<typeof factFindAnswerChangeSchema>;

/** The changes of a refresh fact find, and the newest SCRIVENER summary of them. */
export const refreshChangeSummarySchema = z.object({
    previousVersionId: z.string(),
    previousReference: z.string().nullable(),
    /** The changes between the answers on file and the last completed fact find. */
    changes: z.array(factFindAnswerChangeSchema),
    /**
     * The questions that the last completed fact find answers and the new
     * fact find does not answer yet. They are not changes.
     */
    notAnsweredCount: z.number().int(),
    /** The newest summary of SCRIVENER, or null when it has not run. */
    summary: z
        .object({
            id: z.string(),
            text: z.string(),
            generatedAt: z.string(),
            /** True when an answer changed after the run. */
            stale: z.boolean(),
        })
        .nullable(),
});
export type RefreshChangeSummaryDto = z.infer<typeof refreshChangeSummarySchema>;
